import type SearchPrediction from 'data/search-predictions';
import { findRegionByPlaceId } from 'utils/google-maps/geoLocator';
import { SearchPredictionSource } from 'utils/google-maps/types';
import { useRouter } from 'next/router';

interface UseCommercialSearchPredictionClickProps {
  onClose?: () => void;
  onSearch: (query: string) => void;
  // Map adjustment callback
  onMapRegionChange: (region: mapkit.CoordinateRegion | undefined) => void;
  // Behavior options (removed openInNewTab - listings always open in new tab)
}

/**
 * Custom hook to handle commercial search prediction clicks
 * Follows the standard search pattern for consistency
 * 
 * This hook handles:
 * 1. Commercial listing predictions (always open in new tab)
 * 2. Location predictions (update filters and search query)
 * 3. General search predictions (update query)
 */
export const useCommercialSearchPredictionClick = ({
  onClose,
  onSearch,
  onMapRegionChange,
}: UseCommercialSearchPredictionClickProps) => {
  const router = useRouter();
  const handlePredictionClick = (searchPrediction: SearchPrediction) => {
    const group = searchPrediction.group as string;
    
    // Handle commercial listing predictions
    if (group === 'listings') {
      // Always open listing predictions in a new tab/page
      router.push(`/listings/${searchPrediction.urlPath}`);
      // Close the search panel
      onClose?.();
      return;
    }

    // Handle Google location predictions (locations group from Google)
    if (group === 'locations' && searchPrediction.source === SearchPredictionSource.GOOGLE) {
      // Get coordinates from Google Places API using place_id
      const handleGoogleLocationClick = async () => {
        try {
          const coordinates = await findRegionByPlaceId(searchPrediction.id);
          if (coordinates && onMapRegionChange) {
            onMapRegionChange(coordinates);
          }
        } catch (error) {
          console.error('Failed to get coordinates for Google location:', error);
        }
      };
      
      // Execute the location handling
      handleGoogleLocationClick();
      // Update the search query
      if (onSearch) {
        onSearch(searchPrediction.description);
      }      
      // Close the search panel so map is visible
      onClose?.();
      return;
    }
    
    // Handle general location and other predictions
    if (searchPrediction.description && onSearch) {
      onSearch(searchPrediction.description);
    }
    
    // Close the search panel so map is visible
    onClose?.();
  };

  return { handlePredictionClick };
};
